import React, { useState } from 'react';
import { X, Sparkles, AlertCircle, CheckCircle, TrendingUp, Lightbulb } from 'lucide-react';
import aiEmailService, { EmailGenerationRequest, EmailGenerationResponse } from '../../../services/aiEmailService';
import { toast } from 'react-hot-toast';

interface AIEmailGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUseEmail: (subject: string, body: string) => void;
}

const AIEmailGeneratorModal: React.FC<AIEmailGeneratorModalProps> = ({
  isOpen,
  onClose,
  onUseEmail
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<EmailGenerationResponse | null>(null);
  const [selectedVariation, setSelectedVariation] = useState<number>(-1);

  const [formData, setFormData] = useState<EmailGenerationRequest>({
    purpose: '',
    targetAudience: '',
    companyInfo: '',
    keyPoints: [''],
    tone: 'professional',
    length: 'medium',
    includeCallToAction: true,
    avoidSpamTriggers: true
  });

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!formData.purpose || !formData.targetAudience) {
      toast.error('Please fill in purpose and target audience');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await aiEmailService.generateEmail({
        ...formData,
        keyPoints: formData.keyPoints?.filter(p => p.trim() !== '') || [],
        useSpintax: true,
        spintaxAlternativesPerSentence: 4,
      });
      setGeneratedEmail(response);
      setSelectedVariation(-1); // -1 means main email selected
      toast.success('Email generated successfully!');
    } catch (error: any) {
      console.error('Email generation error:', error);
      toast.error(error.message || 'Failed to generate email');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseEmail = () => {
    if (!generatedEmail) return;

    let subject: string;
    let body: string;

    if (selectedVariation === -1) {
      subject = generatedEmail.subject;
      body = generatedEmail.body;
    } else {
      const variation = generatedEmail.variations[selectedVariation];
      subject = variation.subject;
      body = variation.body;
    }

    onUseEmail(subject, body);
    onClose();
    toast.success('Email applied to campaign!');
  };

  const getSpamScoreBadge = (score: number) => {
    if (score < 30) {
      return (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1 rounded-full">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Excellent ({score}/100)</span>
        </div>
      );
    } else if (score < 50) {
      return (
        <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Fair ({score}/100)</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-1 rounded-full">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">High Risk ({score}/100)</span>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">AI Email Generator</h2>
              <p className="text-sm text-gray-600">Create high-converting cold emails optimized for deliverability</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!generatedEmail ? (
            // Generation Form
            <div className="space-y-6">
              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purpose <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="e.g., Request a meeting to discuss our analytics platform"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.targetAudience}
                  onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                  placeholder="e.g., Marketing directors at B2B SaaS companies"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Company Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company/Product Info
                </label>
                <textarea
                  value={formData.companyInfo}
                  onChange={(e) => setFormData({ ...formData, companyInfo: e.target.value })}
                  placeholder="Describe your company or product..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Key Points */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Points to Cover
                </label>
                {formData.keyPoints?.map((point, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => {
                        const newPoints = [...(formData.keyPoints || [])];
                        newPoints[index] = e.target.value;
                        setFormData({ ...formData, keyPoints: newPoints });
                      }}
                      placeholder={`Key point ${index + 1}`}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    {index > 0 && (
                      <button
                        onClick={() => {
                          const newPoints = formData.keyPoints?.filter((_, i) => i !== index);
                          setFormData({ ...formData, keyPoints: newPoints });
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setFormData({ ...formData, keyPoints: [...(formData.keyPoints || []), ''] })}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  + Add Key Point
                </button>
              </div>

              {/* Tone and Length */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tone
                  </label>
                  <select
                    value={formData.tone}
                    onChange={(e) => setFormData({ ...formData, tone: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="friendly">Friendly</option>
                    <option value="direct">Direct</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Length
                  </label>
                  <select
                    value={formData.length}
                    onChange={(e) => setFormData({ ...formData, length: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="short">Short (50-100 words)</option>
                    <option value="medium">Medium (100-150 words)</option>
                    <option value="long">Long (150-200 words)</option>
                  </select>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !formData.purpose || !formData.targetAudience}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Email
                  </>
                )}
              </button>
            </div>
          ) : (
            // Generated Results
            <div className="space-y-6">
              {/* Main Email */}
              <div className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedVariation === -1 ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setSelectedVariation(-1)}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Generated Email</h3>
                  {getSpamScoreBadge(generatedEmail.deliverability_analysis.spam_score)}
                </div>
                <div className="bg-white rounded-lg p-4 mb-3">
                  <p className="text-sm font-medium text-gray-500 mb-1">Subject:</p>
                  <p className="font-medium text-gray-900 mb-3">{generatedEmail.subject}</p>
                  <p className="text-sm font-medium text-gray-500 mb-1">Body:</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{generatedEmail.body}</p>
                </div>
                {generatedEmail.best_practices_applied.length > 0 && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                    <span>Applied: {generatedEmail.best_practices_applied.slice(0, 2).join(', ')}</span>
                  </div>
                )}
              </div>

              {/* Variations */}
              {generatedEmail.variations.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Subject Line Variations (A/B Test)</h3>
                  <div className="space-y-2">
                    {generatedEmail.variations.map((variation, index) => (
                      <div
                        key={index}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedVariation === index ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setSelectedVariation(index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{variation.subject}</p>
                          </div>
                          {getSpamScoreBadge(variation.spam_score)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deliverability Tips */}
              {generatedEmail.deliverability_analysis.recommendations.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-2">Deliverability Tips:</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        {generatedEmail.deliverability_analysis.recommendations.map((tip, index) => (
                          <li key={index}>• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setGeneratedEmail(null);
                    setSelectedVariation(-1);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Generate New
                </button>
                <button
                  onClick={handleUseEmail}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 flex items-center justify-center gap-2"
                >
                  <TrendingUp className="w-5 h-5" />
                  Use This Email
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIEmailGeneratorModal;
